import React, { useState, useEffect, useId } from "react";
import "./CustomPhoneInput.css"; // Import your custom styles

export const phoneCountryOptions = [
  { code: "+91", flag: "🇮🇳", country: "India", adjective: "Indian", minLength: 10, maxLength: 10 },
  { code: "+1", flag: "🇺🇸", country: "United States", adjective: "US", minLength: 10, maxLength: 10 },
  { code: "+1", flag: "🇨🇦", country: "Canada", adjective: "Canadian", minLength: 10, maxLength: 10 },
  { code: "+44", flag: "🇬🇧", country: "United Kingdom", adjective: "UK", minLength: 10, maxLength: 10 },
  { code: "+61", flag: "🇦🇺", country: "Australia", adjective: "Australian", minLength: 9, maxLength: 9 },
  { code: "+64", flag: "🇳🇿", country: "New Zealand", adjective: "New Zealand", minLength: 8, maxLength: 10 },
  { code: "+971", flag: "🇦🇪", country: "United Arab Emirates", adjective: "UAE", minLength: 9, maxLength: 9 },
  { code: "+65", flag: "🇸🇬", country: "Singapore", adjective: "Singapore", minLength: 8, maxLength: 8 },
  { code: "+81", flag: "🇯🇵", country: "Japan", adjective: "Japanese", minLength: 10, maxLength: 10 },
  { code: "+86", flag: "🇨🇳", country: "China", adjective: "Chinese", minLength: 11, maxLength: 11 },
  { code: "+27", flag: "🇿🇦", country: "South Africa", adjective: "South African", minLength: 9, maxLength: 9 },
];

const defaultCountry = phoneCountryOptions[0];

const getCountryForValue = (value) => {
  const normalizedValue = String(value || "");
  return (
    [...phoneCountryOptions]
      .sort((a, b) => b.code.length - a.code.length)
      .find((country) => normalizedValue.startsWith(country.code)) || defaultCountry
  );
};

export const validateInternationalPhoneNumber = (value) => {
  const country = getCountryForValue(value);
  const nationalNumber = getNationalNumber(value, country);

  return (
    String(value || "").startsWith(country.code) &&
    nationalNumber.length >= country.minLength &&
    nationalNumber.length <= country.maxLength
  );
};

const getNationalNumber = (value, country) => {
  const numericValue = String(value || "").replace(/\D/g, "");
  const numericCode = country.code.replace(/\D/g, "");

  return numericValue.startsWith(numericCode)
    ? numericValue.slice(numericCode.length)
    : numericValue;
};

const formatNationalNumber = (value) => {
  const numericValue = String(value || "").replace(/\D/g, "");

  if (numericValue.length <= 5) {
    return numericValue;
  }

  return `${numericValue.slice(0, 5)}-${numericValue.slice(5)}`;
};

const generateSafeId = (id, name, alt, label, placeholder, reactId) => {
  if (id) return id;

  const source = name || alt || label || placeholder || reactId;

  const sanitized = source
    ?.toString()
    .trim()
    .toLowerCase()
    .replace(/:/g, "")          // remove React colon ids
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-_]/g, "");

  return sanitized || `field-${reactId.replace(/:/g, "")}`;
};

const CustomPhoneInput = ({
  id,
  name,
  alt,
  label,
  value,
  onChange,
  onBlur,
  error,
  errorMessage,
  helpertext,
  msg,
  onCountryChange,
}) => {

  const reactId = useId();
  const generatedId = generateSafeId(
    id,
    name,
    alt,
    label,
    "Enter phone number",
    reactId
  );

  const [selectedCountry, setSelectedCountry] = useState(getCountryForValue(value));
  const [phoneNumber, setPhoneNumber] = useState(value || defaultCountry.code);

  useEffect(() => {
    if (!value) {
      setPhoneNumber(selectedCountry.code);
      return;
    }

    if (!String(value).startsWith(selectedCountry.code)) {
      setSelectedCountry(getCountryForValue(value));
    }

    setPhoneNumber(value);
  }, [value, selectedCountry.code]);

  const handleCountryChange = (e) => {
    const nextCountry =
      phoneCountryOptions.find((country) => `${country.code}-${country.country}` === e.target.value) ||
      defaultCountry;
    const rawNationalNumber = phoneNumber
      ? getNationalNumber(phoneNumber, selectedCountry)
      : "";
    const currentNationalNumber = rawNationalNumber.slice(0, nextCountry.maxLength);
    const nextValue = `${nextCountry.code}${currentNationalNumber}`;

    setSelectedCountry(nextCountry);
    setPhoneNumber(nextValue);
    onChange?.(nextValue);
    onCountryChange?.(currentNationalNumber ? nextCountry : null);
  };

  const handleInputChange = (e) => {
    const nationalNumber = e.target.value.replace(/\D/g, "").slice(0, selectedCountry.maxLength);
    const nextValue = `${selectedCountry.code}${nationalNumber}`;

    setPhoneNumber(nextValue);
    onChange?.(nextValue);
    onCountryChange?.(nationalNumber ? selectedCountry : null);
  };

  const nationalNumber = getNationalNumber(phoneNumber, selectedCountry).slice(0, selectedCountry.maxLength);
  const displayNationalNumber = formatNationalNumber(nationalNumber);
  const visibleErrorMessage = errorMessage || helpertext || msg;

  return (
    <div className="phone-input">
      <div className="phone-input-inner">
        <select
          className="phone-country-select"
          value={`${selectedCountry.code}-${selectedCountry.country}`}
          onChange={handleCountryChange}
          aria-label="Select country code"
        >
          {phoneCountryOptions.map((country) => (
            <option key={`${country.code}-${country.country}`} value={`${country.code}-${country.country}`}>
              {country.flag} {country.code}
            </option>
          ))}
        </select>
        <input
          type="tel"
          id={generatedId}
          data-testid={generatedId}
          value={displayNationalNumber}
          onChange={handleInputChange}
          onBlur={onBlur}
          className={`custom-input ${error ? "input-error" : ""}`}
          maxLength={selectedCountry.maxLength + 1}
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder="Enter phone number"
          name={name}
        />
      </div>

      {error && <p className="error-message">{visibleErrorMessage}</p>}
    </div>
  );
};

export default CustomPhoneInput;
